import { ConfigurationsStore } from 'nomst/Stores/Configuration/configurations.store';
import {
    PayRangeDisplayType,
    CompaRatioDisplayFormat,
    BaseOfCalculation,
    WorkflowTabs,
    ConfigTabs,
    EligibilityProrationTabs, 
    ConfigViews} from 'typings/enums';
import { IWorkflowConfiguration } from 'typings/workflowConfiguration';
import { WorkflowConfiguration } from 'nomst/Stores/Configuration/workflowConfiguration';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { WorkflowSettingsViewStore } from 'nomst/Stores/Configuration/workflowSettingsView.store';
import { EligibilityProrationRuleView } from 'nomst/Stores/Budget/eligibilityProrationRuleView';
import { BudgetConfigurationViewStore } from 'nomst/Stores/Budget/budgetConfigurationView.store';
import { ICycleNavigation } from 'typings/cycleNavigation';
import { workflowSettingsViewStore } from 'utils/MobxDataManager/singletons';

let configurationsStore: ConfigurationsStore;
let workflowSettingsView: WorkflowSettingsViewStore;
let eligibilityProrationRuleView: EligibilityProrationRuleView;
let budgetConfigurationViewStore: BudgetConfigurationViewStore;

// This is a means of navigating and managing settings throughout the app
// without having to import many large stores into small components

const init = (
    newConfigStore: ConfigurationsStore,
    workflow: WorkflowSettingsViewStore,
    budgetConfig: BudgetConfigurationViewStore) => {
    configurationsStore = newConfigStore;
    workflowSettingsView = workflow;
    eligibilityProrationRuleView = budgetConfig.eligibilityProrationRuleView;
    budgetConfigurationViewStore = budgetConfig;
};

const setConfigurationTab = (tab: ConfigTabs): void => {
    configurationsStore.setConfigurationTab(tab);
};

const setupCycleConfigurationView = async (nav: ICycleNavigation): Promise<void> => {
    await fetchCycleConfigurations(nav.cycleId);
    await workflowSettingsViewStore.initData();
    configurationsStore.setCurrentConfigViews(nav.view);
    configurationsStore.setConvertedCycleSettingsViewCycle(nav.cycleId);
    budgetConfigurationViewStore.handleBudgetView(nav.amountCode ?? '', configurationsStore.currentConfigView);
    workflowSettingsViewStore.changeDetailTabLocal(nav.workflowView as WorkflowTabs);
    budgetConfigurationViewStore.handlerulesPanelView(nav.rulesPanel, nav.rulesTab as EligibilityProrationTabs);
};

const setConvertedCycleSettingsViewCycle = (cycleId: number): void => {
    configurationsStore.setConvertedCycleSettingsViewCycle(cycleId);
};

const setCurrenctCycleConfigurationView = (view : ConfigViews): void => {
    configurationsStore.setCurrentConfigViews(view);
};

const toggleShowPerformanceRating = (value: boolean): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.setShowPerformanceRating(value);
    return configurationsStore.toggleShowPeformanceRating(value);
};

const toggleEditPerformanceRating = (value: boolean): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.setEditPerformanceRating(value);
    return configurationsStore.togglePeformanceRatingEdit(value);
};

const toggleDisableCompMemoDownload = (value: boolean): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.setDisableCompMemoDownload(value);
    return configurationsStore.toggleAllowCompMemoDownload(value);
};

const toggleShowPayRange = (value: boolean): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.setShowPayRange(value);
    return configurationsStore.toggleShowPayRange(value);
};

const setPayRangeDisplayType = (value: PayRangeDisplayType): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.setPayRangeDisplayType(value);
    return configurationsStore.setPayRangeDisplayType(value);
};

const setCompRatioDisplayFormat = (value: CompaRatioDisplayFormat): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.setCompRatioDisplayFormat(value);
    return configurationsStore.setCompRatioDisplayFormat(value);
};

const setBaseOfCalculation = (value: BaseOfCalculation): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.setBaseOfCalculation(value);
    return configurationsStore.setBaseOfCalculation(value);
};

const toggleShowTccFields = (value: boolean): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.setTccDisplayValue(value);
    return configurationsStore.setTccDisplayValue(value);
};

const addRemoveFieldsForTccCalc = (value: string[], remove: boolean): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.addRemoveFieldsForTccCalc(value, remove);
    return configurationsStore.addRemoveFieldsForTccCalc(Array.from(
        configurationsStore.convertedCycleSettingsViewCycle.teamPageViews.allExcludedFields.values()));
};

const toggleUseCompSummaryFields = (value: boolean): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.setUseCompSummaryFields(value);
    return configurationsStore.toggleUseCompSummaryFields(value);
};

const toggleHideIneligibleEmployees = (value: boolean): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.setHideIneligibleEmployees(value);
    return configurationsStore.toggleHideIneligibleEmployees(value);
};

const setExcludedCompSummaryFields = (fields: string[]): Promise<void> => {
    const excludedFields =
        configurationsStore.convertedCycleSettingsViewCycle.calculateExcludedCompSummaryFields(fields);
    configurationsStore.convertedCycleSettingsViewCycle.setExcludedCompSummaryFields(excludedFields);
    return configurationsStore.setExcludedCompSummaryFields(excludedFields);
};

const toggleShowIncreaseRecommendation = (value: boolean): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.setShowIncreaseRecommendation(value);
    return configurationsStore.toggleShowIncreaseRecommendation(value);
};

const toggleManagerIncreaseDecision = (value: boolean): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.setManagerIncreaseDecision(value);
    return configurationsStore.toggleManagerIncreaseDecision(value);
};

const setManagerIncreasePercentageUnder = (value: number): Promise<void> => {
    let numberOfValue = Number(value);
    if (isNaN(numberOfValue)) {
        numberOfValue = 0;
    }
    configurationsStore.convertedCycleSettingsViewCycle.setManagerIncreasePercentageUnder(numberOfValue);
    return configurationsStore.setManagerIncreasePercentageUnder(numberOfValue);
};

const setManagerIncreasePercentageOver = (value: number): Promise<void> => {
    let numberOfValue = Number(value);
    if (isNaN(numberOfValue)) {
        numberOfValue = 0;
    }
    configurationsStore.convertedCycleSettingsViewCycle.setManagerIncreasePercentageOver(numberOfValue);
    return configurationsStore.setManagerIncreasePercentageOver(numberOfValue);
};

const togglePreFillDecision = (value: boolean): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.setPreFillDecision(value);
    return configurationsStore.setPreFillDecision(value);
};

const setPreFillDecisionFields = (fields: string[]): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.setPreFillDecisionFields(fields);
    return configurationsStore.setPreFillDecisionFields(fields);
};

const toggleLockViewForManagers = (value: boolean): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.toggleLockViewForManagers(value);
    return configurationsStore.toggleLockViewForManagers(value);
};

const setCycleEndDate = (value: string): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.setEndDate(value);
    return configurationsStore.setCycleEndDate(value);
};

const setPayEffectiveDate = (value: string): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.setPayEffectiveDate(value);
    return configurationsStore.setCyclePayEffectiveDate(value);
};

const setConfigurationComment = (value: string): Promise<void> => {
    configurationsStore.convertedCycleSettingsViewCycle.setComment(value);
    return configurationsStore.setConfigurationComment(value);
};


const fetchCycleConfigurations = async (cycleId: number): Promise<void> => {
    await configurationsStore.fetchCycleConfigurations();
    configurationsStore.setConvertedCycleSettingsViewCycle(cycleId);
};

const fetchCurrentCycleConfiguration = async (): Promise<void> => {
    await configurationsStore.fetchCurrentCycleConfiguration();
};

const navThroughWorkflowView = (tabs: WorkflowTabs): void => {
    workflowSettingsViewStore.changeDetailTabLocal(tabs);
    configurationsStore.navigateCycleSettingsView(getCycleNavigationObject());
};

const removeHrReviewer = (levelName: Nullable<string>, assignedUserId: Nullable<string>): void => {
    configurationsStore.removeHrReviewerUser(
        levelName ?? '', 
        assignedUserId ?? '');
};

const saveWorkflowConfiguration = async (workflowConfig: WorkflowConfiguration): Promise<void> => {
    workflowConfig.saveRules();
    const workflowToSave = {
        rules: workflowConfig.rules,
        hasDepartment: workflowConfig.hasDepartment,
        hasDivision: workflowConfig.hasBusinessUnit,
        hasBusinessUnit: workflowConfig.hasBusinessUnit
    } as IWorkflowConfiguration;
    await configurationsStore.saveWorkflowConfiguration(workflowToSave);
};

const previewCycle = async (): Promise<void> => {
    await configurationsStore.setCycleConfigurationStatusToOpen();
};

const openCycle = async (): Promise<void> => {
    await configurationsStore.handleNotifyManagersCall();
};

const closeCycle = async (): Promise<void> => {
    await configurationsStore.approveClosePlan(true);
};

const publishCycle = async (): Promise<void> => {
    await configurationsStore.setCycleConfigurationStatusToPublished();
};

const navigateToTeamDataSummary = (): void => {
    configurationsStore.setConfigurationTab(ConfigTabs.DATA);
    configurationsStore.navigateConfigurationView();
};

const getCycleNavigationObject = (): ICycleNavigation => {
    return {
        cycleId: configurationsStore.convertedCycleSettingsViewCycle.cycleId,
        view: configurationsStore.currentConfigView,
        amountCode: budgetConfigurationViewStore.currentBudgetView,
        workflowView: workflowSettingsView._tab,
        rulesPanel: eligibilityProrationRuleView.showEligibilityProrationRuleDialog,
        rulesTab: eligibilityProrationRuleView.currentRuleTab
    } as ICycleNavigation;
};

const setCurrentBudgetView = (budgetCode: string, navigate: boolean = false): void => {
    budgetConfigurationViewStore.setCurrentBudgetView(budgetCode);
    if (!navigate) {
        return;
    }
    configurationsStore.navigateCycleSettingsView(getCycleNavigationObject());
};

const setRulesTab = (tab: EligibilityProrationTabs): void => {
    eligibilityProrationRuleView.changeRuleTabLocal(tab);
    configurationsStore.navigateCycleSettingsView(getCycleNavigationObject());
};

const setRulesPanelView = (value: boolean): void => {
    eligibilityProrationRuleView.setShowEligiblityProrationRuleDialog(value);
    configurationsStore.navigateCycleSettingsView(getCycleNavigationObject());
};

const getActiveConfiguration = (): Configuration => {
    return configurationsStore.convertedCycleSettingsViewCycle;
};

export {
    init,
    toggleShowPerformanceRating,
    toggleEditPerformanceRating,
    toggleShowPayRange,
    setPayRangeDisplayType,
    setCompRatioDisplayFormat,
    setBaseOfCalculation,
    toggleShowTccFields,
    addRemoveFieldsForTccCalc,
    toggleUseCompSummaryFields,
    toggleHideIneligibleEmployees,
    setExcludedCompSummaryFields,
    toggleShowIncreaseRecommendation,
    toggleManagerIncreaseDecision,
    setManagerIncreasePercentageUnder,
    setManagerIncreasePercentageOver,
    togglePreFillDecision,
    setPreFillDecisionFields,
    toggleLockViewForManagers,
    setCycleEndDate,
    setConfigurationComment,
    fetchCycleConfigurations,
    setPayEffectiveDate,
    fetchCurrentCycleConfiguration,
    navThroughWorkflowView,
    removeHrReviewer,
    saveWorkflowConfiguration,
    previewCycle,
    openCycle,
    closeCycle,
    publishCycle,
    navigateToTeamDataSummary,
    setCurrentBudgetView,
    setRulesTab,
    setRulesPanelView,
    getActiveConfiguration,
    setConfigurationTab,
    setCurrenctCycleConfigurationView,
    setupCycleConfigurationView,
    setConvertedCycleSettingsViewCycle,
    toggleDisableCompMemoDownload
};
